import { useNavigate } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import {
  IconFlightService,
  IconHotelService,
  IconTripService,
} from "@/components/Icons";
import {
  FlightSearchTrackingEvents,
  HotelSearchTrackingEvents,
  rudderAnalytics,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import type { SearchScope } from "@/types/types";

import { SearchNavigationButton } from "./SearchNavigationButton";

const eventMap = {
  flights: FlightSearchTrackingEvents.USER_SWITCHED_TO_FLIGHT_SEARCH,
  hotels: HotelSearchTrackingEvents.USER_SWITCHED_TO_HOTEL_SEARCH,
  trips: TripTrackingEvents.USER_SWITCHED_TO_TRIPS,
} as const;

export const SearchNavigationButtons = () => {
  const intl = useIntl();

  const navigate = useNavigate();

  const handleClick = (scope: SearchScope) => {
    if (eventMap[scope]) {
      rudderAnalytics.track(eventMap[scope]);
    }

    if (scope === "flights") {
      navigate({
        to: "/flights/search",
      });
    } else if (scope === "hotels") {
      navigate({
        to: "/hotels/search",
      });
    } else if (scope === "trips") {
      navigate({
        to: "/trips/search",
      });
    }
  };

  return (
    <div className="flex gap-6">
      <SearchNavigationButton onClick={() => handleClick("flights")}>
        <IconFlightService className="size-9 shrink-0" />
        {intl.formatMessage({
          defaultMessage: "Flights",
          description: "flights.title",
        })}
      </SearchNavigationButton>

      {useFeatureIsOn("hotels-flow") && (
        <SearchNavigationButton onClick={() => handleClick("hotels")}>
          <IconHotelService className="size-9 shrink-0" />
          {intl.formatMessage({
            defaultMessage: "Hotels",
            description: "hotels.title",
          })}
        </SearchNavigationButton>
      )}

      {useFeatureIsOn("trips-flow") && (
        <SearchNavigationButton
          onClick={() => handleClick("trips")}
          badgeContent={intl.formatMessage({
            defaultMessage: "New",
            description: "trips.newBadge",
          })}
        >
          <IconTripService className="size-9 shrink-0" />
          {intl.formatMessage({
            defaultMessage: "Trips",
            description: "trips.title",
          })}
        </SearchNavigationButton>
      )}
    </div>
  );
};
